import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useEffect, useMemo, useRef } from 'react';
import ReactECharts from 'echarts-for-react';
import { graphic } from 'echarts';
import { COLORS, FONT_BODY, withAlpha } from '../ReimbursementReceiptsCustomTab/palette';
import { currencyFormatter } from 'src/utils/translator';

// Charts for the Utility Allocation Export (Metabase cards #343 / #340 / #341).
// Same ECharts base + ResizeObserver pattern as ReimbursementReceiptsCustomTab/charts.jsx.

const axisLabel = {
  color: COLORS.textMuted,
  fontFamily: FONT_BODY,
  fontSize: 11
};
const axisLine = {
  lineStyle: {
    color: COLORS.border
  }
};
const splitLine = {
  lineStyle: {
    color: COLORS.border,
    type: 'dashed'
  }
};
const usd = v => typeof v === 'number' ? currencyFormatter(v) : v;
const tooltip = extra => _objectSpread({
  backgroundColor: 'rgba(255,255,255,0.97)',
  borderColor: COLORS.borderStrong,
  borderWidth: 1,
  padding: [10, 14],
  textStyle: {
    color: COLORS.text,
    fontFamily: FONT_BODY,
    fontSize: 12
  },
  extraCssText: 'border-radius: 6px; box-shadow: 0 10px 30px rgba(33,41,52,0.14);'
}, extra);
const vGradient = function (hex) {
  let topAlpha = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0.95;
  let botAlpha = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0.55;
  return new graphic.LinearGradient(0, 0, 0, 1, [{
    offset: 0,
    color: withAlpha(hex, topAlpha)
  }, {
    offset: 1,
    color: withAlpha(hex, botAlpha)
  }]);
};
const hGradient = function (hex) {
  let leftAlpha = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0.55;
  let rightAlpha = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0.95;
  return new graphic.LinearGradient(0, 0, 1, 0, [{
    offset: 0,
    color: withAlpha(hex, leftAlpha)
  }, {
    offset: 1,
    color: withAlpha(hex, rightAlpha)
  }]);
};
const ECHARTS_STYLE = {
  height: '100%',
  width: '100%'
};
const Chart = _ref => {
  let option = _ref.option,
    _ref$height = _ref.height,
    height = _ref$height === void 0 ? 300 : _ref$height;
  const ref = useRef(null);
  const boxRef = useRef(null);
  useEffect(() => {
    const fire = () => {
      var _ref$current, _ref$current$getEchar;
      return (_ref$current = ref.current) === null || _ref$current === void 0 || (_ref$current$getEchar = _ref$current.getEchartsInstance) === null || _ref$current$getEchar === void 0 || (_ref$current$getEchar = _ref$current$getEchar.call(_ref$current)) === null || _ref$current$getEchar === void 0 ? void 0 : _ref$current$getEchar.resize();
    };
    const raf = requestAnimationFrame(fire);
    const t = setTimeout(fire, 90);
    let roRaf = 0;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(roRaf);
      roRaf = requestAnimationFrame(fire);
    });
    if (boxRef.current) ro.observe(boxRef.current);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t);
      cancelAnimationFrame(roRaf);
      ro.disconnect();
    };
  }, []);
  const animated = useMemo(() => _objectSpread({
    animationDuration: 760,
    animationEasing: 'cubicOut'
  }, option), [option]);
  return /*#__PURE__*/React.createElement("div", {
    ref: boxRef,
    style: {
      height
    }
  }, /*#__PURE__*/React.createElement(ReactECharts, {
    ref: ref,
    option: animated,
    style: ECHARTS_STYLE,
    opts: {
      renderer: 'canvas'
    },
    notMerge: true
  }));
};

// Coverage Health — trailing 12 months of invoice volume (card #343).
export const CoverageBar = _ref2 => {
  let data = _ref2.data,
    _ref2$height = _ref2.height,
    height = _ref2$height === void 0 ? 240 : _ref2$height;
  const option = useMemo(() => ({
    tooltip: tooltip({
      trigger: 'axis',
      formatter: ps => {
        const d = ps[0];
        const row = data[d.dataIndex] || {};
        return "".concat(d.axisValue, "<br/><b>").concat(row.invoices || 0, "</b> invoices \xB7 ").concat(row.properties || 0, " properties<br/>").concat(usd(row.invoiced || 0), " invoiced");
      }
    }),
    grid: {
      top: 18,
      right: 16,
      bottom: 40,
      left: 44,
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: data.map(d => d.month),
      axisLabel: _objectSpread(_objectSpread({}, axisLabel), {}, {
        interval: 0,
        rotate: data.length > 8 ? 30 : 0
      }),
      axisLine,
      axisTick: {
        show: false
      }
    },
    yAxis: {
      type: 'value',
      axisLabel,
      splitLine,
      axisLine: {
        show: false
      }
    },
    series: [{
      type: 'bar',
      data: data.map(d => Number(d.invoices) || 0),
      barMaxWidth: 30,
      itemStyle: {
        color: vGradient(COLORS.teal),
        borderRadius: [3, 3, 0, 0]
      },
      emphasis: {
        focus: 'series'
      }
    }]
  }), [data]);
  return /*#__PURE__*/React.createElement(Chart, {
    option: option,
    height: height
  });
};

// Unrecovered by Service — vertical bars, red (unrecovered). Card #340.
export const ServiceVarianceBar = _ref3 => {
  let data = _ref3.data,
    _ref3$height = _ref3.height,
    height = _ref3$height === void 0 ? 280 : _ref3$height;
  const option = useMemo(() => ({
    tooltip: tooltip({
      trigger: 'axis',
      axisPointer: {
        type: 'shadow'
      },
      valueFormatter: usd
    }),
    grid: {
      top: 18,
      right: 18,
      bottom: 50,
      left: 56,
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: data.map(d => d.service),
      axisLabel: _objectSpread(_objectSpread({}, axisLabel), {}, {
        interval: 0,
        hideOverlap: true,
        rotate: data.length > 5 ? 30 : 0
      }),
      axisLine,
      axisTick: {
        show: false
      }
    },
    yAxis: {
      type: 'value',
      axisLabel: _objectSpread(_objectSpread({}, axisLabel), {}, {
        formatter: usd
      }),
      splitLine,
      axisLine: {
        show: false
      }
    },
    series: [{
      name: 'Unrecovered',
      type: 'bar',
      data: data.map(d => Number(d.variance) || 0),
      barMaxWidth: 38,
      itemStyle: {
        color: vGradient(COLORS.unpaid),
        borderRadius: [3, 3, 0, 0]
      },
      emphasis: {
        focus: 'series'
      }
    }]
  }), [data]);
  return /*#__PURE__*/React.createElement(Chart, {
    option: option,
    height: height
  });
};

// Top Properties by Unrecovered Variance — horizontal bars (card #341, "row" display).
export const TopPropertiesBar = _ref4 => {
  let data = _ref4.data,
    _ref4$height = _ref4.height,
    height = _ref4$height === void 0 ? 280 : _ref4$height;
  const option = useMemo(() => {
    const ordered = [...data].reverse(); // ECharts y-axis draws bottom-up; reverse so #1 is on top
    return {
      tooltip: tooltip({
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        },
        valueFormatter: usd
      }),
      grid: {
        top: 10,
        right: 24,
        bottom: 24,
        left: 10,
        containLabel: true
      },
      xAxis: {
        type: 'value',
        axisLabel: _objectSpread(_objectSpread({}, axisLabel), {}, {
          formatter: usd
        }),
        splitLine,
        axisLine: {
          show: false
        }
      },
      yAxis: {
        type: 'category',
        data: ordered.map(d => d.property),
        axisLabel: _objectSpread(_objectSpread({}, axisLabel), {}, {
          width: 150,
          overflow: 'truncate'
        }),
        axisLine,
        axisTick: {
          show: false
        }
      },
      series: [{
        name: 'Unrecovered',
        type: 'bar',
        data: ordered.map(d => Number(d.variance) || 0),
        barMaxWidth: 18,
        itemStyle: {
          color: hGradient(COLORS.unpaid),
          borderRadius: [0, 3, 3, 0]
        },
        label: {
          show: true,
          position: 'right',
          formatter: p => usd(p.value),
          color: COLORS.textMuted,
          fontSize: 11
        },
        emphasis: {
          focus: 'series'
        }
      }]
    };
  }, [data]);
  return /*#__PURE__*/React.createElement(Chart, {
    option: option,
    height: height
  });
};